import { accessToken, signOut } from './auth'
import { config } from './config'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface ProblemDetail {
  detail?: string
  code?: string
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await accessToken()
  if (!token) {
    await signOut()
    throw new ApiError(401, 'unauthorized', 'Not signed in')
  }

  const response = await fetch(`${config.apiBaseUrl}/${path.replace(/^\//, '')}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${token}`,
      ...init.headers,
    },
  })

  if (response.status === 401) {
    // The token is gone or rejected; there is nothing to retry with.
    await signOut()
    throw new ApiError(401, 'unauthorized', 'Session expired')
  }

  if (!response.ok) {
    let problem: ProblemDetail = {}
    try {
      problem = (await response.json()) as ProblemDetail
    } catch {
      // A non-JSON error body (a proxy error page, say) is not worth surfacing.
    }
    throw new ApiError(response.status, problem.code, problem.detail ?? `Request failed (${response.status})`)
  }

  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, params?: Record<string, string | number | undefined>) => {
    const query = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== '')
            .map(([key, value]) => [key, String(value)]),
        ).toString()
      : ''
    return request<T>(`${path}${query}`)
  },
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
